import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Sparkles } from 'lucide-react';
import { STATUSES } from '../../../constants/doctorProfile';
import InterestPicker from '../../InterestPicker';
import * as SelfApi from '../selfApi';
import { mediaUrl } from '../../../utils/mediaUrl';
import { downscalePhoto } from '../../../utils/profilePhoto';

const BLUE = '#47ABDB';

const Form = styled.form`display:flex;flex-direction:column;gap:1rem;`;
const FormGroup = styled.div`display:flex;flex-direction:column;`;
const Label = styled.label`margin-bottom:3px;font-weight:bold;font-size:.9rem;color:#334155;`;
const Input = styled.input`padding:8px;border:1px solid #ccc;font-size:.9rem;&:disabled{background-color:#f3f4f6;color:#6b7280;}`;
const Select = styled.select`padding:8px;border:1px solid #ccc;font-size:.9rem;`;
const Textarea = styled.textarea`padding:8px;border:1px solid #ccc;font-size:.9rem;font-family:inherit;min-height:5.5rem;`;
const ButtonRow = styled.div`display:flex;gap:.75rem;align-items:center;`;
const SubmitButton = styled.button`background-color:${BLUE};color:#fff;border:none;padding:10px 20px;font-size:1rem;cursor:pointer;&:hover{background-color:#0056b3;}&:disabled{opacity:.6;cursor:not-allowed;}`;
const CancelButton = styled.button`background:none;border:1px solid #cbd5e1;color:#334155;padding:10px 20px;font-size:1rem;cursor:pointer;&:hover{background:#f1f5f9;}`;
const SectionTitle = styled.h3`margin:.5rem 0 0;font-size:1rem;font-weight:700;color:#1f2937;`;
const Checks = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.4rem;@media(max-width:480px){grid-template-columns:1fr;}`;
const CheckLabel = styled.label`display:flex;gap:.4rem;align-items:flex-start;font-weight:normal;font-size:.85rem;`;
const Message = styled.p`font-size:.875rem;color:${p => (p.error ? '#b91c1c' : '#065f46')};`;
const PhotoPreview = styled.img`margin-top:.6rem;width:96px;height:96px;border-radius:50%;object-fit:cover;border:2px solid ${BLUE};background:#eef2f7;`;
const PhotoActions = styled.div`display:flex;gap:.6rem;align-items:center;margin-top:.6rem;flex-wrap:wrap;`;
const PhotoButton = styled.button`background:#eef2f7;color:#111D4E;border:1px solid #cbd5e1;padding:6px 12px;font-size:.85rem;font-weight:600;cursor:pointer;&:hover:not(:disabled){background:#e2e8f0;}&:disabled{opacity:.6;cursor:not-allowed;}`;
const PhotoRemove = styled.button`background:none;border:none;color:#b91c1c;text-decoration:underline;cursor:pointer;font-size:.82rem;padding:0;&:disabled{opacity:.6;cursor:not-allowed;}`;
const PhotoHint = styled.div`font-size:.75rem;color:#64748b;margin-top:.35rem;`;
const PhotoError = styled.p`font-size:.8rem;color:#b91c1c;margin:.4rem 0 0;`;
const UpdatedBadge = styled.span`font-size:.68rem;font-weight:700;color:#166534;background:#dcfce7;padding:.1rem .4rem;margin-left:.4rem;text-transform:uppercase;letter-spacing:.02em;`;
const SuggestionCard = styled.div`margin-top:.4rem;padding:.65rem .75rem;background:#eff9fe;border:1px solid ${BLUE};border-radius:4px;display:flex;gap:.55rem;align-items:flex-start;`;
const SuggestionIcon = styled.div`color:${BLUE};flex-shrink:0;margin-top:.15rem;`;
const SuggestionBody = styled.div`flex:1;min-width:0;`;
const SuggestionLabel = styled.div`font-size:.72rem;font-weight:700;color:#0f4c68;text-transform:uppercase;letter-spacing:.02em;margin-bottom:.3rem;`;
const SuggestionValue = styled.div`font-size:.85rem;color:#1f2937;white-space:pre-wrap;word-break:break-word;`;
const FilledNote = styled.div`font-size:.75rem;color:#64748b;margin-top:.25rem;`;
const ClassificationHint = styled.div`font-size:.78rem;color:#64748b;margin-top:.35rem;`;
const UseValueButton = styled.button`margin-top:.55rem;background:${BLUE};color:#fff;border:none;padding:6px 14px;font-size:.8rem;font-weight:600;cursor:pointer;&:hover{background:#0056b3;}`;

// Shared by ProfileEditModal.js (the "Edit my profile" popup) and
// SpeakerInviteConfirmation.js (the first-time speaker onboarding page) - one
// set of fields, own component, so the two call sites can't drift apart.
export const editableFields = [
  ['title', 'Title'], ['firstName', 'First Name'], ['lastName', 'Last Name'],
  ['institution', 'Institution / practice / clinic'], ['specialty', 'Specialty'],
  ['streetAddress', 'Street Address'], ['city', 'City'], ['state', 'State/Province'],
  ['postalCode', 'Postal Code'],
];

// The label the profile shows for each profile key, as [message id, English default]. A key that is not
// here (titleSuffix, phone: no field in this form) is never named in the "filled in by the organizers" hint.
export const fieldLabels = {
  title: ['form.title', 'Title'], firstName: ['form.firstName', 'First Name'], lastName: ['form.lastName', 'Last Name'],
  institution: ['form.institution', 'Institution / Clinic / Company'], specialty: ['form.specialty', 'Specialty'],
  streetAddress: ['form.streetAddress', 'Street Address'], city: ['form.city', 'City'], state: ['form.state', 'State/Province'],
  postalCode: ['form.postalCode', 'Postal Code'], country: ['form.country', 'Country'],
  photoUrl: ['profile.photo.label', 'Profile photo'],
  professionalStatuses: ['speaker.onboarding.roles', 'Profile classification'],
  interests: ['speakerMgmt.interestsCsv', 'Cardiac interests'],
  bioEn: ['speaker.field.shortBioEn', 'Short bio (English)'], bioDe: ['speaker.field.shortBioDe', 'Short bio (German)'],
  motivationEn: ['speaker.field.motivationShortEn', 'Motivation (English)'], motivationDe: ['speaker.field.motivationShortDe', 'Motivation (German)'],
  linkedinUrl: ['speaker.field.linkedin', 'LinkedIn'], websiteUrl: ['speaker.field.website', 'Website'],
  facebookUrl: ['speaker.field.facebook', 'Facebook / Meta'], instagramUrl: ['speaker.field.instagram', 'Instagram'],
};

const ProfileFormFields = ({
  draft, countryOptions, message, saving, t, roleLabel,
  onChange, onToggleList, onInterestsChange, onSubmit, onCancel,
  submitLabel, cancelLabel, fieldDiffs, filledByOrganizers,
}) => {
  // GET /api/me's quiet hint: a small muted note under a field the organizers wrote and the person has not
  // changed since. Gone once a save makes the person's own value the field's source.
  const filledNote = (name) => (filledByOrganizers || []).includes(name)
    && <FilledNote>{t('profile.filledByOrganizersNote', 'Filled in by the organizers')}</FilledNote>;
  // Optional (see SpeakerInviteConfirmation.js): a field that changed shows
  // a small "updated" marker, and - only when there was a genuine previous
  // value, not just a gap being filled in - a one-click way to revert it,
  // right where the field already is instead of a separate decision screen.
  // ProfileEditModal.js passes it only right after AttendeeProfile.js's own
  // resolve step found something; otherwise it's a no-op there.
  const diffFor = (name) => (fieldDiffs || []).find((d) => d.field === name);
  // Only a genuinely blank-then-filled field was actually updated already
  // (see SpeakerInviteConfirmation.js's resolve step) - a field that
  // already had something real of its own keeps that value, so it isn't
  // "Updated" here, just has a proposed value available (see hintFor).
  const badgeFor = (name) => { const d = diffFor(name); return d && !d.currentValue && <UpdatedBadge>{t('profile.fieldUpdated', 'Updated')}</UpdatedBadge>; };
  const fileRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  // Picking a photo: downscale in the browser (works on smartphones - see
  // utils/profilePhoto.js), upload the small JPEG, and put its URL into the
  // same photoUrl field the rest of the form saves. Uploading only stores a
  // file - the account changes when the form is saved, so cancelling the
  // edit leaves the profile untouched.
  const handlePhotoPicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      const ready = await downscalePhoto(file);
      // POST /api/me/profile/photo-file only stores the file, as the old upload did; the form's save
      // (PUT /api/me/profile with photoUrl) is what applies it.
      const photoUrl = await SelfApi.storeProfilePhotoFile(ready);
      onChange({ target: { name: 'photoUrl', value: photoUrl } });
    } catch (err) {
      console.error('Profile photo upload failed:', err);
      setPhotoError(err.response?.data || t('profile.photo.error', 'Could not upload the photo. Please try another image.'));
    } finally {
      setPhotoBusy(false);
    }
  };
  // A field with a real current value never gets silently overwritten (see
  // SpeakerInviteConfirmation.js's resolve step) - what's shown in the form
  // is already the account's own current value, safe by default. If
  // something else was proposed for it too (an admin edit, AI research), it
  // shows here as something to opt into, not something already applied
  // that needs reverting.
  const hintFor = (name) => {
    const d = diffFor(name);
    if (!d?.currentValue || d.proposedValue === d.currentValue) return null;
    return (
      <SuggestionCard>
        <SuggestionIcon><Sparkles size={16} /></SuggestionIcon>
        <SuggestionBody>
          <SuggestionLabel>{t('profile.proposedValueAvailable', 'Newer value suggested')}</SuggestionLabel>
          <SuggestionValue>{d.proposedValue}</SuggestionValue>
          <UseValueButton type="button" onClick={() => onChange({ target: { name, value: d.proposedValue } })}>
            {t('profile.useProposedValue', 'Use this value')}
          </UseValueButton>
        </SuggestionBody>
      </SuggestionCard>
    );
  };
  return (
  <Form onSubmit={onSubmit}>
    <FormGroup>
      <Label>{t('form.email', 'Email Address')}</Label>
      <Input value={draft.email || ''} disabled />
    </FormGroup>
    {editableFields.map(([name, label]) => (
      <FormGroup key={name}>
        <Label htmlFor={name}>{t(`form.${name}`, label)}{badgeFor(name)}</Label>
        <Input id={name} name={name} value={draft[name] || ''} onChange={onChange} />
        {hintFor(name)}
        {filledNote(name)}
      </FormGroup>
    ))}
    <FormGroup>
      <Label htmlFor="country">{t('form.country', 'Country')}{badgeFor('country')}</Label>
      <Select id="country" name="country" value={draft.country || ''} onChange={onChange}>
        <option value="">{t('form.select', 'Select...')}</option>
        {/* Older profiles may hold a raw legacy country string that predates
            this picker (e.g. from the original public form) - keep it
            selectable as-is instead of silently blanking the field. */}
        {draft.country && !countryOptions.some((c) => c.name === draft.country) && (
          <option value={draft.country}>{draft.country}</option>
        )}
        {countryOptions.map((c) => (
          <option key={c.code} value={c.name}>{c.name}</option>
        ))}
      </Select>
      {hintFor('country')}
      {filledNote('country')}
    </FormGroup>

    <FormGroup>
      <Label htmlFor="photoFile">{t('profile.photo.label', 'Profile photo')}{badgeFor('photoUrl')}</Label>
      {draft.photoUrl && (
        <PhotoPreview src={mediaUrl(draft.photoUrl)} alt="" referrerPolicy="no-referrer" onError={(e) => { e.target.style.visibility = 'hidden'; }} onLoad={(e) => { e.target.style.visibility = 'visible'; }} />
      )}
      <PhotoActions>
        <PhotoButton type="button" disabled={photoBusy || saving} onClick={() => fileRef.current && fileRef.current.click()}>
          {photoBusy ? t('profile.photo.uploading', 'Uploading…') : t('profile.photo.choose', 'Choose photo')}
        </PhotoButton>
        {draft.photoUrl && (
          <PhotoRemove type="button" disabled={photoBusy || saving} onClick={() => onChange({ target: { name: 'photoUrl', value: '' } })}>
            {t('profile.photo.remove', 'Remove photo')}
          </PhotoRemove>
        )}
        <input ref={fileRef} id="photoFile" type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoPicked} />
      </PhotoActions>
      {photoError && <PhotoError>{photoError}</PhotoError>}
      <PhotoHint>{t('profile.photo.hint', 'JPG, PNG or WebP - we resize your photo automatically.')}</PhotoHint>
      {hintFor('photoUrl')}
      {filledNote('photoUrl')}
    </FormGroup>

    <SectionTitle>{t('speaker.onboarding.roles', 'Profile classification')}</SectionTitle>
    <Checks>
      {STATUSES.map((status) => (
        <CheckLabel key={status}>
          <input type="checkbox" checked={(draft.professionalStatuses || []).includes(status)} onChange={() => onToggleList('professionalStatuses', status)} />
          {roleLabel(status)}
        </CheckLabel>
      ))}
    </Checks>
    <ClassificationHint>
      {t('profile.classificationHint', 'Describes the person in their profile; it is not a program role. Whether someone speaks at an event comes from the program and its invitations.')}
    </ClassificationHint>
    {filledNote('professionalStatuses')}

    <SectionTitle>{t('speaker.onboarding.interests', 'Cardiac interests (choose at least 3)')}</SectionTitle>
    <InterestPicker value={draft.interests} onChange={onInterestsChange} />
    {filledNote('interests')}

    <SectionTitle>{t('signup.optionalDetails', 'Optional details')}</SectionTitle>
    <FormGroup>
      <Label htmlFor="bioEn">{t('speaker.field.shortBioEn', 'Short bio (English)')}{badgeFor('bioEn')}</Label>
      <Textarea id="bioEn" name="bioEn" value={draft.bioEn || ''} onChange={onChange} />
      {hintFor('bioEn')}
      {filledNote('bioEn')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="bioDe">{t('speaker.field.shortBioDe', 'Short bio (German)')}{badgeFor('bioDe')}</Label>
      <Textarea id="bioDe" name="bioDe" value={draft.bioDe || ''} onChange={onChange} />
      {hintFor('bioDe')}
      {filledNote('bioDe')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="motivationEn">{t('speaker.field.motivationEn', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because (English)')}{badgeFor('motivationEn')}</Label>
      <Textarea id="motivationEn" name="motivationEn" value={draft.motivationEn || ''} onChange={onChange} />
      {hintFor('motivationEn')}
      {filledNote('motivationEn')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="motivationDe">{t('speaker.field.motivationDe', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because (German)')}{badgeFor('motivationDe')}</Label>
      <Textarea id="motivationDe" name="motivationDe" value={draft.motivationDe || ''} onChange={onChange} />
      {hintFor('motivationDe')}
      {filledNote('motivationDe')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="linkedinUrl">{t('speaker.field.linkedin', 'LinkedIn')}{badgeFor('linkedinUrl')}</Label>
      <Input type="url" id="linkedinUrl" name="linkedinUrl" value={draft.linkedinUrl || ''} onChange={onChange} />
      {hintFor('linkedinUrl')}
      {filledNote('linkedinUrl')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="websiteUrl">{t('speaker.field.website', 'Website')}{badgeFor('websiteUrl')}</Label>
      <Input type="url" id="websiteUrl" name="websiteUrl" value={draft.websiteUrl || ''} onChange={onChange} />
      {hintFor('websiteUrl')}
      {filledNote('websiteUrl')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="facebookUrl">{t('speaker.field.facebook', 'Facebook / Meta')}{badgeFor('facebookUrl')}</Label>
      <Input type="url" id="facebookUrl" name="facebookUrl" value={draft.facebookUrl || ''} onChange={onChange} />
      {hintFor('facebookUrl')}
      {filledNote('facebookUrl')}
    </FormGroup>
    <FormGroup>
      <Label htmlFor="instagramUrl">{t('speaker.field.instagram', 'Instagram')}{badgeFor('instagramUrl')}</Label>
      <Input type="url" id="instagramUrl" name="instagramUrl" value={draft.instagramUrl || ''} onChange={onChange} />
      {hintFor('instagramUrl')}
      {filledNote('instagramUrl')}
    </FormGroup>

    {message && <Message error={message.error}>{message.text}</Message>}
    <ButtonRow>
      <SubmitButton type="submit" disabled={saving}>
        {saving ? t('profile.saving', 'Saving…') : (submitLabel || t('profile.save', 'Save changes'))}
      </SubmitButton>
      {onCancel && (
        <CancelButton type="button" onClick={onCancel}>
          {cancelLabel || t('speaker.hub.cancel', 'Cancel')}
        </CancelButton>
      )}
    </ButtonRow>
  </Form>
  );
};

export default ProfileFormFields;
